import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface UserAddress {
  latitude: number;
  longitude: number;
  address: string;
  zipCode: string;
}

interface PlusCode {
  compound_code: string;
  global_code: string;
}

interface GoogleGeocodeResponse {
  plus_code?: PlusCode;
  results: Array<{
    formatted_address: string;
    address_components: Array<{
      long_name: string;
      short_name: string;
      types: string[];
    }>;
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
    };
    plus_code?: PlusCode;
  }>;
  status: string;
}

@Injectable({
  providedIn: 'root',
})
export class GeocodingService {
  private readonly apiKey = 'AIzaSyBwMI_s76c1eUO1ByBIFwDJgY-lys9pup0';
  private readonly geocodeUrl =
    'https://maps.googleapis.com/maps/api/geocode/json';

  constructor(private http: HttpClient) {}

  public reverseGeocode(
    latitude: number,
    longitude: number
  ): Observable<UserAddress | null> {
    const url = `${this.geocodeUrl}?latlng=${latitude},${longitude}&key=${this.apiKey}&language=en_US&result_type=street_address`;

    return this.http.get<GoogleGeocodeResponse>(url).pipe(
      map((response) => {
        console.log(
          '🚀 ~ GeocodingService ~ reverseGeocode ~ response:',
          response
        );
        if (response.status === 'OK' && response.results.length > 0) {
          const result = response.results[0];
          const addressComponents = result.address_components;

          // Extract compound_code from plus_code (preferred) or from result's plus_code
          const plusCode =
            response.plus_code || result.plus_code;
          let address = '';

          if (plusCode?.compound_code) {
            // Extract location name from compound_code
            // Format: "QMMX+5X2 District 1, Ho Chi Minh City, Vietnam"
            // We want: "District 1, Ho Chi Minh City, Vietnam"
            const compoundCodeParts = plusCode.compound_code.split(' ', 2);
            if (compoundCodeParts.length > 1) {
              // Remove the plus code part (first part) and use the rest
              address = plusCode.compound_code.substring(
                compoundCodeParts[0].length + 1
              );
            } else {
              address = plusCode.compound_code;
            }
          }

          // Fallback to extracting locality and administrative_area_level_1
          if (!address) {
            const locality = addressComponents.find((component) =>
              component.types.includes('locality')
            )?.long_name;
            const administrativeArea = addressComponents.find((component) =>
              component.types.includes('administrative_area_level_1')
            )?.short_name;

            address =
              locality && administrativeArea
                ? `${locality}, ${administrativeArea}`
                : result.formatted_address;
          }

          const postalCode = addressComponents.find((component) =>
            component.types.includes('postal_code')
          )?.long_name;

          return {
            latitude: result.geometry.location.lat,
            longitude: result.geometry.location.lng,
            address: address,
            zipCode: postalCode || '',
          };
        } else {
          throw new Error('No address found');
        }
      }),
      catchError((error) => {
        console.error('Geocoding error:', error);
        return throwError(() => error);
      })
    );
  }
}
